import React from 'react';
import PropTypes from 'prop-types';
import { forbidExtraProps } from 'airbnb-prop-types';
import cx from 'classnames';
import isTouchDevice from 'is-touch-device';
import openDirectionShape from './shapes/OpenDirectionShape';
import { OPEN_DOWN, OPEN_UP } from './constants';
import announce from '../../../helpers/announce';

const propTypes = forbidExtraProps({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    inputValue: PropTypes.string,
    // screenReaderMessage: PropTypes.string,
    focused: PropTypes.bool,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    readOnly: PropTypes.bool,
    openDirection: openDirectionShape,
    showCaret: PropTypes.bool,
    hasError: PropTypes.bool,
    errorMessage: PropTypes.string,
    
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyDownShiftTab: PropTypes.func,
    onKeyDownTab: PropTypes.func,

    onKeyDownArrowDown: PropTypes.func,
    onKeyDownQuestionMark: PropTypes.func,

    // accessibility
    isFocused: PropTypes.bool // describes actual DOM focus
});

const defaultProps = {
    placeholder: 'Select Date',
    inputValue: '',
    focused: false,
    disabled: false,
    required: false,
    readOnly: null,
    openDirection: OPEN_DOWN,
    showCaret: false,
    hasError: false,
    errorMessage: '',

    onBlur() {},
    // onChange() {},
    onFocus() {},
    onKeyDownShiftTab() {},
    onKeyDownTab() {},

    onKeyDownArrowDown() {},
    onKeyDownQuestionMark() {},

    // accessibility
    isFocused: false
};

export default class DateInput extends React.Component {
    constructor(props) {
        super(props);
        
        this.state = {
            dateString: props.inputValue ? props.inputValue :  '',
            isTouchDevice: false,
            placeholder: props.placeholder
        };

        this.onChange = this.onChange.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.setInputRef = this.setInputRef.bind(this);
    }

    componentDidMount() {
        // eslint-disable-next-line react/no-did-mount-set-state
        this.setState({ isTouchDevice: isTouchDevice() });
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.inputValue && nextProps.inputValue !== this.state.dateString) {
            this.setState({
                dateString: nextProps.inputValue
            });
        }
        
        if (!nextProps.inputValue && !nextProps.hasError) {
            this.setState({
                dateString: ''
            });
        }
    }

    componentDidUpdate(prevProps) {
        const { focused, isFocused } = this.props;
        if (prevProps.focused === focused && prevProps.isFocused === isFocused) return;

        if (focused && isFocused) {
            this.inputRef.focus();
            this.inputRef.select();
        }
    }
    
    onFocus = () => {
        this.setState({
            placeholder: 'MM/DD/YYYY'
        });
        
        this.props.onFocus();
    }
    
    onBlur = () => {
        const { onBlur, placeholder } = this.props;
        const { dateString } = this.state;
        
        this.setState({
            placeholder
        });
        onBlur(dateString);
    }

    onChange(e) {
        const { onKeyDownQuestionMark } = this.props;
        const dateString = e.target.value;
        const allowedChars = /^[0-9/-]*$/;
        // In Safari, onKeyDown does not consistently fire ahead of onChange. As a result, we need to
        // special case the `?` key so that it always triggers the appropriate callback, instead of
        // modifying the input value
        if (dateString[dateString.length - 1] === '?') {
            onKeyDownQuestionMark(e);
        }
        
        if (dateString === '' || allowedChars.test(dateString)) {
            this.setState({ dateString });
            // onChange(dateString);
        }
        else {
            announce('Character not allowed', 'assertive');
        }
    }

    onKeyDown(e) {
        e.stopPropagation();
        
        const { key } = e;
        const { onKeyDownShiftTab, onKeyDownTab, onKeyDownArrowDown, onKeyDownQuestionMark } = this.props;

        if (key === 'Tab') {
            if (e.shiftKey) {
                onKeyDownShiftTab(this.state.dateString);
            }
            else {
                onKeyDownTab(this.state.dateString);
            }
        }
        else if (key === 'ArrowDown') {
            onKeyDownArrowDown();
        }
        else if (key === '?') {
            e.preventDefault();
            onKeyDownQuestionMark();
        }
    }

    setInputRef(ref) {
        this.inputRef = ref;
    }

    render() {
        const { dateString, isTouchDevice: isTouch } = this.state;
        const {
            id,
            // placeholder,
            // screenReaderMessage,
            focused,
            showCaret,
            // onFocus,
            disabled,
            required,
            readOnly,
            openDirection,
            label,
            hasError,
            errorMessage
        } = this.props;

        // const screenReaderMessageId = `DateInput__screen-reader-message-${id}`;
        const inputClasses = cx('sarsa-date-picker-input', {
            'error': hasError
        });

        return (
            <div
                className={cx('DateInput', {
                    'DateInput--with-caret': showCaret && focused,
                    'DateInput--disabled': disabled,
                    'DateInput--open-down': openDirection === OPEN_DOWN,
                    'DateInput--open-up': openDirection === OPEN_UP
                })}>
                <label
                    className="rec-sr-only"
                    htmlFor={id}>
                    {`${label} - (MM/DD/YYYY)`}
                </label>
                <input
                    aria-label={`${label} ${errorMessage} - Enter a date or press the down arrow to interact with the calendar.`}
                    aria-invalid={hasError}
                    className={inputClasses}
                    type="text"
                    id={id}
                    name={id}
                    maxLength="10"
                    ref={this.setInputRef}
                    value={dateString}
                    onChange={this.onChange}
                    onBlur={this.onBlur}
                    onKeyDown={this.onKeyDown}
                    onFocus={this.onFocus}
                    placeholder={this.state.placeholder}
                    autoComplete="off"
                    disabled={disabled}
                    readOnly={typeof readOnly === 'boolean' ? readOnly : isTouch}
                    required={required} />

                {/* {screenReaderMessage && (
                    <p id={screenReaderMessageId} className="screen-reader-only">
                        {screenReaderMessage}
                    </p>
                )} */}

            </div>
        );
    }
}

DateInput.propTypes = propTypes;
DateInput.defaultProps = defaultProps;
